<template>
  <div class="products">
    <v-row dense wrap>
      <v-col cols="12">
        <h1 class="display-1">{{ $t("PRODUCTS_HEADER") }}</h1>
        <br>
        <p class="subheading" v-html="$t('PRODUCTS_TEXT')"></p>
      </v-col>
      <v-col v-if="false" cols="12" class="whiteCard christmas">
        <v-row wrap row>
          <v-col class="iconFlex">
            <img :src="christmasImage" class="christmasImage"/>
          </v-col>
          <v-col class="textFlex">
            <p class="text"><b>Tipp: </b>Weihnachten steht vor der Tür! Profitieren Sie von unserer großen Auswahl an <b>Geschenkprodukten</b> weiter unten auf der Seite, um Ihren Liebsten eine Freude zu machen.</p>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row wrap align="center">
      <v-col cols="12" md="4">
        <v-alert v-if="paymentMethodActive('KLARNA_LATER')" type="success" class="elevation-3 paymentPromotion" :value="true">
          {{ $t('PAY_PER_BILL_NOTICE') }}
        </v-alert>
      </v-col>
      <v-col cols="12" md="4">
        <DiscountBanner/>
      </v-col>
      <v-col cols="12" md="4" class="topButtonRow">
        <v-row wrap justify="end">
          <v-col cols="auto">
            <v-btn class="selectionButton" @click="goToStep(2)">
              <v-icon>arrow_back</v-icon>
              {{ $t("BUTTON_BACK_TO_IMAGES") }}
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn class="selectionButton" @click="goToStep(1)">
              <v-icon>add</v-icon>
              {{ $t("BUTTON_OTHER_CODE") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <br>
    
    <!-- Product Category Tabs -->
    <v-row wrap v-if="availableCategories.length > 0">
      <v-col cols="12">
        <v-tabs v-model="selectedCategoryIndex" class="category-tabs" hide-slider>
          <v-tab class="category-chip">
            <v-icon v-if="false" left small>mdi-view-grid</v-icon>
            {{ $t('PRODUCTS_ALL_CATEGORIES') }}
          </v-tab>
          <v-tab v-for="category in availableCategories" :key="category.id" :class="['category-chip', { 'highlight-chip': category.highlight }]">
            <v-icon v-if="category.iconName" left small>{{ category.iconName }}</v-icon>
            {{ category.name }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>
    
    <v-row wrap class="layout product-row" justify="center">
      <v-col v-for="product in filteredProducts" :key="product.id" class="product-col">
        <span v-if="hasDiscount(product)" class="discountBadge">
          {{ formatDiscount(getDiscount(product)) }}
        </span>
        <v-card :class="{productCard: true, highlight: product.highlight}">
          <v-img
              :src="product.imageUrl"
              class="productImage"
              contain
              lazy-src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAN4AAADjCAMAAADdXVr2AAAAA1BMVEX///+nxBvIAAAASElEQVR4nO3BMQEAAADCoPVPbQdvoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAOA8W9AAHiq1isAAAAAElFTkSuQmCC"></v-img>
          <v-card-title class="productHeader">
            {{ product.name }}
          </v-card-title>
          <v-card-subtitle>
            {{ product.subHeading }}
          </v-card-subtitle>

          <v-card-text class="productShortDescription">
            <p class="text--primary product-description" v-html="product.shortDescription"/>
          </v-card-text>

          <v-card-actions class="actions">
            <span :class="{priceTag: true, discounted: hasDiscount(product)}">{{ formatProductPrice(product) }}</span>
            <span class="priceTag instead" v-if="hasDiscount(product)">
              {{ formatPrice(product.setPriceInsteadOf) }}
            </span>
            <v-spacer/>
            <v-btn color="primary" :to="`/product/${product.id}`">{{ $t("BUTTON_SHOW_PRODUCT") }}</v-btn>
          </v-card-actions>

        </v-card>
      </v-col>
    </v-row>
    
    <!-- Show All Products Button -->
    <v-row v-if="selectedCategory !== null" justify="center" class="show-all-row">
      <v-col cols="auto">
        <v-btn large outlined color="primary" @click="showAllProducts">
          {{ $t('PRODUCTS_SHOW_ALL') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import Component from "vue-class-component";
import BasicComponent from "@/global/BasicComponent";
import DiscountBanner from "@/components/DiscountBanner.vue";
import {ProductDto, ProductCategoryDto} from "@swagger";
import christmasImage from "@/assets/christmas.svg";
import {Watch} from "vue-property-decorator";

@Component({
  components: {DiscountBanner}
})
export default class Products extends BasicComponent {
  christmasImage = christmasImage;
  selectedCategoryIndex: number = 0;
  shouldScrollToTop: boolean = false;
  isLoadingFromQuery: boolean = false;

  created() {
    this.requestProductCategories();
    this.loadCategoryFromQuery();
  }
  
  loadCategoryFromQuery() {
    this.isLoadingFromQuery = true;
    const catId = this.$route.query.cat;
    if (catId) {
      // Find the index of the category in availableCategories
      const categoryIndex = this.availableCategories.findIndex(cat => cat.id === Number(catId));
      if (categoryIndex !== -1) {
        this.selectedCategoryIndex = categoryIndex + 1; // +1 because index 0 is "All Categories"
      }
    } else {
      this.selectedCategoryIndex = 0;
    }
    this.$nextTick(() => {
      this.isLoadingFromQuery = false;
    });
  }
  
  @Watch('selectedCategoryIndex')
  onCategoryChange(newIndex: number, oldIndex: number) {
    // Don't update URL if this change came from loadCategoryFromQuery
    if (this.isLoadingFromQuery) {
      return;
    }
    
    // Build new URL with updated query parameter
    const url = new URL(window.location.href);
    
    if (newIndex === 0) {
      // "All Products" selected - remove query parameter
      url.searchParams.delete('cat');
    } else {
      // Specific category selected - set query parameter
      const category = this.availableCategories[newIndex - 1];
      if (category) {
        url.searchParams.set('cat', String(category.id));
      }
    }
    
    // Update URL without triggering navigation (and thus no scroll)
    window.history.replaceState({}, '', url.toString());
    
    // Only scroll to top if explicitly requested (via "Show All" button)
    if (this.shouldScrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.shouldScrollToTop = false;
    }
  }
  
  @Watch('$route.query.cat')
  onQueryChange() {
    this.loadCategoryFromQuery();
  }
  
  get selectedCategory(): number | null {
    if (this.selectedCategoryIndex === 0) {
      return null;
    }
    return this.availableCategories[this.selectedCategoryIndex - 1]?.id || null;
  }
  
  set selectedCategory(index: number) {
    this.selectedCategoryIndex = index;
  }

  mounted() {
    //if (this.$store.getters.step === "3") this.trackPage("Produkte");
  }

  get products() {
    return this.$store.getters.products;
  }

  get productCategories() {
    return this.$store.getters.productCategories;
  }

  get availableCategories() {
    // Only show categories that have products
    return this.productCategories.filter(category => 
      this.products.some(product => product.categoryIds && product.categoryIds.includes(category.id))
    );
  }

  get filteredProducts() {
    if (this.selectedCategory === null) {
      return this.products;
    }
    return this.products.filter(product => 
      product.categoryIds && product.categoryIds.includes(this.selectedCategory)
    );
  }

  formatProductPrice(product: ProductDto): string {
    return `${product.detailedConfigurationSurcharge > 0 ? this.$t('PRICE_PREFIX_FROM') + ' ' : ''}${this.formatPrice(product.price)}`;
  }

  getImageUrl(image) {
    var images = require.context('@/assets/', false, /\.jpg$/);
    return images('./' + image);
  }

  getDiscount(product) {
    let instead = product.setPriceInsteadOf;
    if (!instead) instead = product.price;
    return product.price / instead - 1;
  }

  hasDiscount(product) {
    return this.getDiscount(product) <= -0.01; // At least 1%
  }

  formatDiscount(discount) {
    return `${Math.round(discount * 100)}%`
  }

  showAllProducts() {
    this.shouldScrollToTop = true;
    this.selectedCategoryIndex = 0;
  }

}
</script>
<style scoped lang="scss">

@import "../stylesheets/Variables.scss";

$highlight-border-width: 5px;
$highlight-shimmer-size: 400%;
$highlight-shimmer-duration: 4s;

.v-alert.paymentPromotion {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-top: none;
  border-radius: 5px;
  background-color: white !important;
  color: black;
}

.topButtonRow {
  height: 100%;
  justify-content: flex-end;

  .flex {
    flex: 0 1 auto;
    align-self: end;
  }
}

.product-row {
  margin-bottom: 5px;

  .product-col {
    flex-grow: 0;
    flex-shrink: 1;
    position: relative;
    
    .discountBadge {
      background-color: $price-color-discount;
      color: white;
      font-weight: bold;
      font-size: 16px;
      text-align: center;

      z-index: 100;

      $height: 44px;
      height: $height;
      width: $height;
      border-radius: calc(#{$height} / 2);
      line-height: $height;

      position: absolute;
      top: -10px;
      right: -5px;
      
      @media screen and (min-width: 769px) {
        right: 26px;
      }
    }

      .productCard {
      width: 320px;
      max-width: 98%;
      margin: 4px 30px 30px 4px;
      display: inline-block;
      vertical-align: top;
      position: relative;
      height: 100%;
      
      @media screen and (max-width: 768px) {
        margin: 4px 4px 30px 4px;
      }

      &.highlight {
        overflow: hidden;
        
        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: $highlight-shimmer-size;
          height: $highlight-shimmer-size;
          background: conic-gradient(
            from 0deg,
            rgba(255, 215, 0, 0.6) 0deg,
            rgba(255, 165, 100, 0.6) 60deg,
            rgba(255, 105, 180, 0.6) 120deg,
            rgba(200, 100, 200, 0.6) 180deg,
            rgba(147, 112, 219, 0.6) 240deg,
            rgba(200, 160, 120, 0.6) 300deg,
            rgba(255, 215, 0, 0.6) 360deg
          );
          animation: rotate-shimmer $highlight-shimmer-duration linear infinite;
          pointer-events: none;
          z-index: 1;
          border-radius: 4px;
          filter: blur(3px);
          opacity: 0.8;
        }
        
        &::after {
          content: '';
          position: absolute;
          top: $highlight-border-width;
          left: $highlight-border-width;
          right: $highlight-border-width;
          bottom: $highlight-border-width;
          background: white;
          border-radius: 2px;
          z-index: 2;
        }
      }
      
      .v-image,
      .v-card__title,
      .v-card__subtitle,
      .v-card__text,
      .v-card__actions {
        position: relative;
        z-index: 3;
      }

      .productImage {
        width: 300px;
        height: 300px;
        margin: $highlight-border-width auto;

        max-width: 100%;
      }

      .productHeader {
        word-break: break-word;
      }

      .productShortDescription {
        padding-top: 0;

        .product-description {
          margin-bottom: 0;
        }
      }

      .actions {
        position: absolute;
        bottom: 0;
        width: 100%;
      }
    }
  }
}

.priceTag {
  color: $price-color !important;
  margin-left: 10px;
  margin-bottom: 0px;
  font-size: 18px;
  text-overflow: unset;
  overflow: visible;

  &.discounted {
    color: $price-color-discount !important;
  }

  &.instead {
    color: grey !important;
    margin-left: 0;
    font-size: 14px;
    float: right;
    text-decoration: line-through;
    text-align: right;
    padding-right: 10px;
    width: auto;
    min-width: 23%;
    line-height: 22px;
  }
}

.christmas {
  padding: 10px 10px 0;
  border: 3px solid rgb(191, 162, 15);
  margin-bottom: 10px;

  .row {
    margin-bottom: 0;
  }

  .v-col > * {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
  }

  .iconFlex {
    flex: 0 1;

    @media screen and (max-width: 1400px) {
      margin: 0 auto;
    }

    .christmasImage {
      $size: 75px;
      height: $size;
      border: 4px solid rgba(183, 63, 47, 0.8);
      border-radius: $size;
      padding: 6px;
      position: relative;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .textFlex {
    .text {
      font-size: 16px;
      margin-left: 20px;
      margin-bottom: 0;
      position: relative;
      top: 50%;
      transform: translateY(-50%);
    }
  }

}

.category-tabs {
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    ::v-deep .v-tabs-bar {
      display: flex;
      flex-direction: column;
      align-items: center;
      height: auto !important;
    }
    
    ::v-deep .v-slide-group__wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    
    ::v-deep .v-slide-group__content {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .category-chip {
      padding: 8px 16px !important;
    }
  }
  
  .category-chip {
    border-radius: 25px;
    margin: 4px 6px;
    padding: 0 16px;
    background-color: #f5f5f5;
    transition: all 0.3s;
    
    &.v-tab--active {
      background-color: var(--v-primary-base) !important;
      color: white !important;
      
      .v-icon {
        color: white !important;
      }
    }
    
    &:not(.v-tab--active):hover {
      background-color: #e0e0e0;
    }
    
    &.highlight-chip {
      position: relative !important;
      overflow: hidden !important;
      
      &::before {
        content: '' !important;
        position: absolute !important;
        top: 0 !important;
        width: 100% !important;
        height: 100% !important;
        opacity: 1 !important;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 215, 0, 0.8) 25%,
          rgba(255, 105, 180, 0.8) 50%,
          rgba(147, 112, 219, 0.8) 75%,
          transparent 100%
        ) !important;
        animation: shimmer 3s infinite !important;
        pointer-events: none !important;
        mix-blend-mode: screen !important;
      }
      
      &.v-tab--active::before {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.4) 25%,
          rgba(255, 255, 255, 0.6) 50%,
          rgba(255, 255, 255, 0.4) 75%,
          transparent 100%
        ) !important;
      }
    }
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  66.67% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes rotate-shimmer {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.show-all-row {
  margin-top: 20px;
  margin-bottom: 30px;
}

</style>

<style lang="scss">

.productShortDescription {
  p {
    margin-bottom: 0;
  }
}

.paymentPromotion {
  i {
    color: rgba(23, 146, 19, 0.9) !important;
  }
}

</style>